import { Button, Card, Tag, Tooltip } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { Scrollbars } from 'react-custom-scrollbars'
import React, { useMemo } from 'react'
import styles from './ActionsList.module.scss'
import { getBadBranchText, processActionName, toFixed } from '../../../helpers/helpers'
import { CardImage } from '../../../components/cards/CardImage'
import { PositionCard } from '../../Report/PositionCard'
import { HandsTrainLink } from '../../../Trainer/TrainerSettingsButton/TrainerSettingsButton'
import { useComponentLocale } from '../../../hooks/useComponentLocale'

// generate actions flow
function ActionsList({ filter, handTab, handData, isRangeMode, isHideOOPHand }) {
	const actions = []

	const getLocale = useComponentLocale()
	const viewLocale = getLocale({ component: 'topBar', section: 'view' })
	const trainLocale = getLocale({ component: 'common_buttons', section: 'train' })
	const heroLocale = getLocale({ component: 'common', section: 'hero' })
	const playedLocale = getLocale({ component: 'hand_result', section: 'played' })
	const villGtoFreqLocale = getLocale({ component: 'hand_result', section: 'villain_gto_freq' })
	const villGtoRangeLocale = getLocale({ component: 'hand_result', section: 'villain_gto_range' })
	const diffBetLocale = getLocale({ component: 'hand_result', section: 'diff_bet' })

	if (!handData.result) {
		return <div>Hand is not calculated</div>
	}

	const { players, positions, result, spotId, isHero: isHeroValue } = handData
	const { trace } = result

	const isHero = useMemo(() => {
		if (['hands'].includes(handTab)) {
			return isHeroValue
		}

		return filter.is_hero ? isHeroValue : isHeroValue.map((value) => !value)
	}, [handTab, filter.is_hero])

	let board = ''

	let index = 0
	trace.forEach((t) => {
		index++
		// get available actions
		const gtoActions = []
		// get available range blocks
		const rangeBlocks = []

		// draw actions
		for (let i = 0; i < t.actions.length; i++) {
			// define spans
			let ev
			let played
			let isGtoModePlayed
			// define action styles
			let actionBoxStyle = null
			// define action styles if there are cards and is_gto_range
			let subActionBoxStyle = null

			// add ev
			if (t.is_ev) {
				ev = (Math.round(t.actions_ev[i] * 100) / 100).toFixed(2)
				ev = ev > 0 ? `+${ev}` : ev
			} else {
				ev = ''
			}

			// 1st case player has cards
			if (t.is_played) {
				// get color for chosen action
				if (i === t.choose) {
					if (t.is_bad_branch_view && t.is_mistake_view) {
						actionBoxStyle = styles.gtoActionLowBBChosen
					} else if (t.is_bad_branch_view && !t.is_mistake_view) {
						actionBoxStyle = styles.gtoActionFineChosen
					} else if (!t.is_bad_branch_view && t.is_mistake_view) {
						actionBoxStyle = styles.gtoActionLowChosen
					} else if (!t.is_bad_branch_view && !t.is_mistake_view) {
						actionBoxStyle = styles.gtoActionFineChosen
					}
				}
				played = toFixed(t.actions_played[i] * 100, 0)
			} else if (t.is_gto_range) {
				// 2nd case - no hand, play vs range
				actionBoxStyle = i === t.choose ? styles.gtoActionUnknownChosen : ''
				played = toFixed(t.actions_gto_range[i] * 100, 0)

				// should not be applied
			} else {
				actionBoxStyle = i === t.choose ? styles.gtoActionUnknownChosen : ''
			}
			/* If player has cards and isGtoRange true*/
			if (t.is_played && t.is_gto_range) {
				subActionBoxStyle = i === t.choose ? styles.gtoActionUnknownChosen : ''
				isGtoModePlayed = toFixed(t.actions_gto_range[i] * 100, 0)
			}

			if (t.is_played && t.is_gto_range && !isHero[t.player] && isHideOOPHand) {
				actionBoxStyle = i === t.choose ? styles.gtoActionUnknownChosen : ''
				played = toFixed(t.actions_gto_range[i] * 100, 0)
			}

			gtoActions.push(
				<div className={[styles.gtoAction, actionBoxStyle].join(' ')}>
					<div className={styles.gtoActionHeader}>
						<span className={styles.gtoActionName}>
							<b>{processActionName(t.actions[i])}</b>
						</span>
						{(isHero[t.player] || (!isHero[t.player] && !isHideOOPHand)) && (
							<span className={styles.gtoActionEvVal}>{ev}</span>
						)}
					</div>
					<div className={styles.gtoActionPlayedVal}>
						<div className={[styles.gtoActionPlayedBg, actionBoxStyle].join(' ')} style={{ width: `${played}%` }} />
						<div className={[styles.gtoActionPlayedText, actionBoxStyle].join(' ')}>
							{played ? `${played}%` : 'UNKNOWN'}
						</div>
					</div>
				</div>
			)

			const renderRangeBlocks = () => (
				<div className={[styles.gtoAction, subActionBoxStyle].join(' ')}>
					<div className={styles.gtoActionHeader}>
						<span className={styles.gtoActionName}>
							<b>{processActionName(t.actions[i])}</b>
						</span>
					</div>
					<div className={styles.gtoActionPlayedVal}>
						<div
							className={[styles.gtoActionPlayedBg, subActionBoxStyle].join(' ')}
							style={{ width: `${isGtoModePlayed}%` }}
						/>
						<div className={[styles.gtoActionPlayedText, subActionBoxStyle].join(' ')}>
							{isGtoModePlayed ? `${isGtoModePlayed}%` : 'UNKNOWN'}
						</div>
					</div>
				</div>
			)

			t.is_played && t.is_gto_range && isRangeMode && !t.isPlayedLine && !isHideOOPHand
				? rangeBlocks.push(renderRangeBlocks())
				: isRangeMode && isHideOOPHand && !t.isPlayedLine && isHero[t.player] && rangeBlocks.push(renderRangeBlocks())
		}

		// check if difference exists
		const diff =
			t.trace_diff_bet !== '' ? (
				<div>
					<span className={styles.actionDiff}>{t.trace_diff_bet}</span>
				</div>
			) : (
				''
			)

		// check if action type or board
		if (t.type !== 'action') {
			// append data to existing board
			board += t.tree_action
			const cards = []

			for (let i = 0; i < board.length; i += 2) {
				cards.push(<CardImage isSmall card={board.substring(i, i + 2)} />)
			}
			actions.push(
				<p className={styles.actionsContainerBoard}>
					<div className={styles.boardBlock}>
						<span>{cards}</span>
						<div className={styles.action}>
							<b>{getLocale({ component: 'common', section: t.type }).toUpperCase()}</b>
							<br />
							<b>POT: {toFixed(t.current_real_pot, 2)}</b>
						</div>
					</div>
				</p>
			)
		} else {
			// constants
			let actionBoardStyle = 'unknown'

			// if player has cards
			if (t.actions_played && t.actions_played.length > 0) {
				if (t.is_bad_branch_view && t.is_mistake_view) {
					actionBoardStyle = styles.diffEvNegBadBranch
				} else if (t.is_bad_branch_view && !t.is_mistake_view) {
					actionBoardStyle = styles.diffEvPos
				} else if (!t.is_bad_branch_view && t.is_mistake_view) {
					actionBoardStyle = styles.diffEvNeg
				} else if (!t.is_bad_branch_view && !t.is_mistake_view) {
					actionBoardStyle = styles.diffEvPos
				}
			}

			// get difference value
			let diff_ev = '0.00'

			if (t.diff_ev !== undefined) {
				diff_ev = toFixed(t.diff_ev, 2) > 0 ? `-${toFixed(t.diff_ev, 2)}` : diff_ev
			} else {
				diff_ev = '?'
			}

			// add action component
			actions.push(
				<Card
					className={styles.actionsContainer}
					bodyStyle={{ padding: 5 }}
					headStyle={{ padding: 5, minHeight: 0 }}
					title={
						<div className={styles.actionTitle}>
							<div className={styles.positionCardContainer}>
								<PositionCard position={positions[t.player]} className={styles.positionCard} />

								<div className={styles.tracesContainer}>
									<b>{players[t.player]}</b>
								</div>

								<div className={styles.tracesRealActionContainer}>
									<span>{t.real_action}</span>
								</div>

								{t.is_bad_branch_view && (
									<span>
										<Tooltip
											title={getBadBranchText(
												t.bad_branch_view_type,
												playedLocale,
												villGtoFreqLocale,
												villGtoRangeLocale,
												diffBetLocale
											)}
											destroyTooltipOnHide
										>
											{(!isHideOOPHand || (isHideOOPHand && isHero[t.player])) && (
												<ExclamationCircleFilled className={styles.exclamationCircleFilled} />
											)}
										</Tooltip>
									</span>
								)}

								<span>{isHero[t.player] && <Tag className={styles.heroTag}>{heroLocale}</Tag>}</span>

								<a href={`/viewer?id=${spotId}&q=${t.query}`} rel='noreferrer' target='_blank'>
									<Button shape='round' size='small' type='link'>
										{viewLocale}
									</Button>
								</a>

								<HandsTrainLink
									query={t.query}
									spotId={spotId}
									spotName={handData.spotName}
									train={trainLocale}
									hero={positions[t.player]}
								/>

								{(!isHideOOPHand || (isHideOOPHand && isHero[t.player])) && (
									<span className={[styles.diffEv, actionBoardStyle].join(' ')}>{diff_ev}</span>
								)}
							</div>

							{diff && <div className={styles.actionTitleVs}>{diff}</div>}
						</div>
					}
					bordered={false}
				>
					<div className={styles.actionBlock}>{gtoActions}</div>
					<div className={[styles.actionBlock, styles.rangeBlocksContainer].join(' ')}>{rangeBlocks}</div>
				</Card>
			)
		}
	})

	return (
		<div className={styles.actionsRowContainer}>
			<Scrollbars
				renderThumbVertical={({ style, ...props }) => (
					<div {...props} style={{ ...style, backgroundColor: '#5a6268', borderRadius: '3px' }} />
				)}
			>
				{actions}
			</Scrollbars>
		</div>
	)
}

export default ActionsList
